import { Fragment, useEffect, useState } from "react";
import { getSourceReport } from "@/lib/apiClient";
import { BarChart3, ChevronRight } from "lucide-react";

const RANGES = [[7, "7 dagen"], [30, "30 dagen"], [90, "90 dagen"], [0, "Alles"]];
const pct = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : "–");

function Row({ r, sub, open, onToggle, testid }) {
  return (
    <tr data-testid={testid} onClick={onToggle} className={`border-t border-[#EDF1F5] ${sub ? "bg-[#F6F9FB] text-[#5B6675]" : "cursor-pointer hover:bg-[#F6F9FB]"}`}>
      <td className={`py-3 pr-3 ${sub ? "pl-10 text-sm" : "pl-4 font-medium text-[#16202B]"}`}>
        {!sub && <ChevronRight className={`inline h-4 w-4 mr-1 text-[#8994A3] transition-transform ${open ? "rotate-90" : ""}`} />}
        {sub ? r.campaign : r.source}
      </td>
      <td className="py-3 px-3 text-right tabular-nums">{r.leads}</td>
      <td className="py-3 px-3 text-right tabular-nums">{r.appointments} <span className="text-xs text-[#8994A3]">({pct(r.appointments, r.leads)})</span></td>
      <td className="py-3 pl-3 pr-4 text-right tabular-nums font-semibold text-[#0C6E6E]">{r.customers} <span className="text-xs font-normal text-[#8994A3]">({pct(r.customers, r.leads)})</span></td>
    </tr>
  );
}

export function SourceReport() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  useEffect(() => { getSourceReport(days).then(setData).catch(() => setData({ rows: [], total: {} })); }, [days]);
  return (
    <section className="rounded-2xl border border-[#E2E7EC] bg-white mb-8 overflow-hidden" data-testid="source-report">
      <div className="flex flex-wrap items-center gap-3 px-6 py-5 border-b border-[#E2E7EC]">
        <BarChart3 className="h-5 w-5 text-[#16BFBF]" />
        <div>
          <h2 className="font-heading font-semibold text-lg text-[#16202B]">Bronrapportage</h2>
          <p className="text-xs text-[#5B6675]">Leads, afspraken en klanten (verkocht pakket of dienst) per bron en campagne</p>
        </div>
        <div className="ml-auto inline-flex rounded-xl border border-[#E2E7EC] p-1">
          {RANGES.map(([d, l]) => (
            <button key={d} data-testid={`source-report-range-${d}`} onClick={() => setDays(d)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${days === d ? "bg-[#001C51] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"}`}>{l}</button>
          ))}
        </div>
      </div>
      {!data ? <p className="px-6 py-6 text-sm text-[#5B6675]">Laden…</p> : data.rows.length === 0 ? (
        <p className="px-6 py-6 text-sm text-[#5B6675]" data-testid="source-report-empty">Geen leads in deze periode.</p>
      ) : (
        <div className="overflow-x-auto fe-scroll">
          <table className="w-full text-sm">
            <thead><tr className="text-xs uppercase tracking-wide text-[#5B6675]">
              <th className="text-left py-3 pl-4 pr-3 font-semibold">Bron / campagne</th>
              <th className="text-right py-3 px-3 font-semibold">Leads</th>
              <th className="text-right py-3 px-3 font-semibold">Afspraken</th>
              <th className="text-right py-3 pl-3 pr-4 font-semibold">Klanten</th>
            </tr></thead>
            <tbody>
              {data.rows.map((r, i) => (
                <Fragment key={r.source}>
                  <Row r={r} open={open[r.source]} onToggle={() => setOpen((o) => ({ ...o, [r.source]: !o[r.source] }))} testid={`source-row-${i}`} />
                  {open[r.source] && r.campaigns.map((c, j) => <Row key={c.campaign} r={c} sub testid={`source-row-${i}-campaign-${j}`} />)}
                </Fragment>
              ))}
              <tr className="border-t-2 border-[#E2E7EC] font-semibold text-[#16202B]" data-testid="source-report-total">
                <td className="py-3 pl-4 pr-3">Totaal</td>
                <td className="py-3 px-3 text-right tabular-nums">{data.total.leads}</td>
                <td className="py-3 px-3 text-right tabular-nums">{data.total.appointments}</td>
                <td className="py-3 pl-3 pr-4 text-right tabular-nums text-[#0C6E6E]">{data.total.customers}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
